import { useToastStore, type Toast } from '@/stores/toast';

const TYPE_STYLES: Record<Toast['type'], string> = {
  info: 'bg-blue-600',
  success: 'bg-green-600',
  warning: 'bg-orange-500',
  error: 'bg-red-600',
};

const PROGRESS_STYLES: Record<Toast['type'], string> = {
  info: 'bg-blue-300',
  success: 'bg-green-300',
  warning: 'bg-orange-300',
  error: 'bg-red-300',
};

const TYPE_ICONS: Record<Toast['type'], string> = {
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  warning:
    'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z',
  error:
    'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
};

// Errors are announced immediately; everything else waits for a pause in speech.
const ARIA_LIVE_MAP: Record<Toast['type'], 'assertive' | 'polite'> = {
  info: 'polite',
  success: 'polite',
  warning: 'polite',
  error: 'assertive',
};

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {toasts.map((toast) => (
        /*
         * Use a <div role="alert"> so the toast is semantically an alert region,
         * not a button. The dismiss action is a separate <button> inside.
         * aria-live is set per-toast so errors interrupt immediately.
         */
        <div
          key={toast.id}
          role="alert"
          aria-live={ARIA_LIVE_MAP[toast.type]}
          aria-atomic="true"
          className={`${TYPE_STYLES[toast.type]} text-white rounded-lg shadow-lg overflow-hidden
            animate-[slideIn_0.3s_ease-out] pointer-events-auto relative`}
        >
          <div className="flex items-start gap-3 px-4 pt-3 pb-2">
            <svg
              className="w-5 h-5 shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={TYPE_ICONS[toast.type]}
              />
            </svg>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{toast.title}</p>
              <p className="text-xs opacity-90 mt-0.5">{toast.message}</p>
            </div>

            {/* Dedicated dismiss button — semantically separate from the alert content */}
            <button
              onClick={() => removeToast(toast.id)}
              className="shrink-0 mt-0.5 opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
              aria-label="Dismiss notification"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress bar — absolutely positioned so it never affects layout */}
          <div className="absolute bottom-0 inset-x-0 h-1 bg-black/10">
            <div
              className={`h-full rounded-full ${PROGRESS_STYLES[toast.type]}`}
              style={{
                animation: `toastProgress ${toast.duration}ms linear forwards`,
              }}
            />
          </div>
          {/* Spacer so content doesn't sit on top of the progress bar */}
          <div className="h-1" />
        </div>
      ))}
    </div>
  );
}
